import { useQuery } from '@tanstack/react-query'
import { Link, useSearchParams } from 'react-router-dom'
import { api } from '../api/client'
import type { ProductPerfOut, ProductPerfRow } from '../api/types'
import { PageHeader } from '../components/Layout'
import { FilterBar } from '../components/FilterBar'
import { Kpi } from '../components/Kpi'
import { Pager } from '../components/Pager'
import { SearchBox } from '../components/SearchBox'
import { Panel } from '../components/States'
import { SortHeader, useSort } from '../components/SortHeader'
import { useFilters, usePaging } from '../lib/filters'
import { count, formatDate, money, percent, rangeLabel } from '../lib/format'

const SORTS = ['revenue', 'units', 'margin', 'margin_pct', 'sell_through'] as const
type Sort = (typeof SORTS)[number]

const COLUMNS: { key: Sort; label: string }[] = [
  { key: 'revenue', label: 'Revenue' },
  { key: 'units', label: 'Units' },
  { key: 'margin', label: 'Margin' },
  { key: 'margin_pct', label: 'Margin %' },
]

export function ProductsPage() {
  const { filters } = useFilters({ allTime: true })
  const { offset, limit, setOffset } = usePaging(25)
  const { sort, direction, sortBy } = useSort<Sort>(SORTS, 'revenue')
  const [params, setParams] = useSearchParams()

  // Two genuinely different questions, so the choice is explicit rather than
  // a checkbox: "what sold" aggregates invoice lines, "whole catalogue" starts
  // from the product list and attaches sales to it.
  const view = params.get('view') === 'catalogue' ? 'catalogue' : 'sold'
  const includeZero = view === 'catalogue'
  const inStockOnly = params.get('instock') === '1'
  const zeroSalesOnly = params.get('nosales') === '1'
  const search = params.get('q') ?? ''

  /** Current query string with these keys set — for the drill-down links. */
  function withParams(extra: Record<string, string>): string {
    const next = new URLSearchParams(params)
    for (const [key, value] of Object.entries(extra)) next.set(key, value)
    next.delete('offset')
    return next.toString()
  }

  /**
   * A product's page, found by its HIKE id — SKUs get edited in HIKE — or,
   * for lines typed in with no product, by their text. This list's filters
   * go along so "All products" comes back to them.
   */
  function productHref(row: ProductPerfRow, hash = ''): string | null {
    const next = new URLSearchParams(params)
    if (row.product_id) next.set('product', row.product_id)
    else if (row.line_key) next.set('line', row.line_key)
    else return null
    return `/products/${encodeURIComponent(row.sku ?? row.name)}?${next.toString()}${hash}`
  }

  function setView(next: 'sold' | 'catalogue') {
    const updated = new URLSearchParams(params)
    if (next === 'catalogue') updated.set('view', 'catalogue')
    else updated.delete('view')
    // in-stock-only and sell-through only mean anything for the catalogue.
    if (next === 'sold') {
      updated.delete('instock')
      updated.delete('nosales')
    }
    updated.delete('offset')
    setParams(updated, { replace: true })
  }

  function setSearch(next: string) {
    const updated = new URLSearchParams(params)
    if (next) updated.set('q', next)
    else updated.delete('q')
    updated.delete('offset')
    setParams(updated, { replace: true })
  }

  function toggle(key: string, on: boolean) {
    const next = new URLSearchParams(params)
    if (on) next.set(key, '1')
    else next.delete(key)
    next.delete('offset')
    setParams(next, { replace: true })
  }

  const query = useQuery({
    queryKey: [
      'products',
      filters.from,
      filters.to,
      filters.outletId,
      filters.scope,
      sort,
      direction,
      includeZero,
      inStockOnly,
      zeroSalesOnly,
      search,
      offset,
      limit,
    ],
    queryFn: () =>
      api.get<ProductPerfOut>('/analytics/products', {
        from_date: filters.from,
        to_date: filters.to,
        outlet_id: filters.outletId,
        scope: filters.scope,
        sort,
        direction,
        include_zero_sales: includeZero,
        in_stock_only: inStockOnly,
        zero_sales_only: zeroSalesOnly,
        search: search || null,
        limit,
        offset,
      }),
  })

  const rows = query.data?.items ?? []
  const summary = query.data?.summary

  // Margin columns are blank both when no cost data exists and when the account
  // may not see margins; either way there is nothing to show, so the columns
  // only appear once some row actually carries a value.
  const showMargin = rows.some((row) => row.margin !== null || row.margin_pct !== null)
  // on_hand/stock_value are populated only in the catalogue-wide view.
  const showStock = rows.some((row) => row.on_hand !== null)

  function header(key: Sort, label: string) {
    return (
      // Keyed because the margin columns below are rendered from a map.
      <SortHeader
        key={key}
        label={label}
        sortKey={key}
        sort={sort}
        direction={direction}
        onSort={sortBy}
        numeric
      />
    )
  }

  return (
    <>
      <PageHeader
        title="Product performance"
        subtitle={`${view === 'sold' ? 'What sold' : 'Whole catalogue'} · ${rangeLabel(
          filters.from,
          filters.to,
        )}`}
      />

      <div className="content stack">
        <FilterBar allTime />

        <div className="row" style={{ alignItems: 'flex-end' }}>
          <SearchBox
            id="product-search"
            label="Search products"
            placeholder="Name or SKU — e.g. rockport, KM6005"
            value={search}
            onChange={setSearch}
          />
        </div>

        <div className="row">
          <div className="seg">
            <button className={view === 'sold' ? 'on' : ''} onClick={() => setView('sold')}>
              What sold
            </button>
            <button
              className={view === 'catalogue' ? 'on' : ''}
              onClick={() => setView('catalogue')}
            >
              Whole catalogue
            </button>
          </div>

          {view === 'catalogue' ? (
            <label className="row" style={{ gap: 6 }}>
              <input
                type="checkbox"
                checked={inStockOnly}
                onChange={(event) => toggle('instock', event.target.checked)}
              />
              In stock only
              <span className="cell-sub">— the slow-mover view</span>
            </label>
          ) : (
            <span className="cell-sub">
              Everything sold in this period, including one-off lines that are not catalogue products.
            </span>
          )}
        </div>

        {zeroSalesOnly ? (
          <div className="alert warn row" style={{ justifyContent: 'space-between' }}>
            <span>
              <strong>Showing only products that did not sell.</strong> These are sitting in stock
              with no sales in this period.
            </span>
            <button className="btn sm" onClick={() => toggle('nosales', false)}>
              Show all catalogue products
            </button>
          </div>
        ) : null}

        {view === 'catalogue' && !zeroSalesOnly && summary?.sold_not_in_catalogue ? (
          <div className="alert info">
            <strong>{count(summary.sold_not_in_catalogue)} products that sold are not shown here.</strong>{' '}
            They were sold on lines carrying no SKU — custom items, charges, ad-hoc entries — so
            they match no catalogue product. Switch to <strong>What sold</strong> to see them.
          </div>
        ) : null}

        {summary ? (
          <div className="grid cols-3">
            {view === 'sold' ? (
              <>
                <Kpi
                  label="Products sold"
                  value={count(summary.products_sold)}
                  foot="distinct products with at least one sale"
                />
                <Kpi
                  label="Not catalogue products"
                  value={count(summary.sold_not_in_catalogue)}
                  foot="sold on lines with no SKU"
                  to={`/products/uncatalogued?${params.toString()}`}
                />
              </>
            ) : (
              <>
                <Kpi
                  label="Catalogue products"
                  value={count(summary.catalogue_total)}
                  foot="active, with a SKU"
                />
                <Kpi
                  label="No sales this period"
                  value={count(summary.zero_sale_products)}
                  foot="catalogue products that did not sell"
                />
                {summary.in_stock_zero_sales !== null ? (
                  <Kpi
                    label="In stock, not selling"
                    value={count(summary.in_stock_zero_sales)}
                    foot="on hand with zero sales in this window"
                    to={`/products?${withParams({ view: 'catalogue', instock: '1', nosales: '1' })}`}
                  />
                ) : null}
              </>
            )}
          </div>
        ) : null}

        <div className="card">
          <div className="card-head">
            <h2>Products</h2>
            <span className="hint">
              {count(query.data?.total)} matching{search ? ` “${search}”` : ''}
            </span>
          </div>
          <div className="card-body flush">
            <Panel
              isLoading={query.isPending}
              error={query.error}
              isEmpty={rows.length === 0}
              emptyText="No products matched these filters."
            >
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>SKU</th>
                      {header('revenue', 'Revenue')}
                      {header('units', 'Units')}
                      <th className="num">Invoices</th>
                      {showMargin ? COLUMNS.slice(2).map((col) => header(col.key, col.label)) : null}
                      {showStock ? <th className="num">On hand</th> : null}
                      <th className="num">Last sold</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => {
                      const href = productHref(row)
                      return (
                        <tr key={row.product_id ?? `line:${row.sku ?? row.name}`}>
                          <td>
                            {href ? (
                              <Link to={href}>
                                <span className="truncate" style={{ display: 'inline-block' }}>
                                  {row.name}
                                </span>
                              </Link>
                            ) : (
                              <span className="truncate" style={{ display: 'inline-block' }}>
                                {row.name}
                              </span>
                            )}
                            {!row.in_catalogue ? (
                              <div>
                                <span className="chip grey">Not in catalogue</span>
                              </div>
                            ) : null}
                          </td>
                          <td className="cell-sub">{row.sku ?? '—'}</td>
                          <td className="num strong">{money(row.revenue)}</td>
                          <td className="num">{count(row.units)}</td>
                          <td className="num">
                            {href && row.invoice_count > 0 ? (
                              <Link to={productHref(row, '#invoices')!} title="See these invoices">
                                {count(row.invoice_count)}
                              </Link>
                            ) : (
                              count(row.invoice_count)
                            )}
                          </td>
                          {showMargin ? (
                            <>
                              <td className="num">
                                {row.margin === null ? <span className="cell-sub">—</span> : money(row.margin)}
                              </td>
                              <td className="num">
                                {percent(row.margin_pct)}
                                {row.cost_coverage_pct !== null && row.cost_coverage_pct < 100 ? (
                                  <div className="cell-sub">
                                    {percent(row.cost_coverage_pct, 0)} costed
                                  </div>
                                ) : null}
                              </td>
                            </>
                          ) : null}
                          {showStock ? (
                            <td className="num">
                              {row.on_hand === null ? <span className="cell-sub">—</span> : count(row.on_hand)}
                              {row.stock_value !== null ? (
                                <div className="cell-sub">{money(row.stock_value)}</div>
                              ) : null}
                            </td>
                          ) : null}
                          <td className="num cell-sub">{formatDate(row.last_sold)}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
              <Pager
                offset={offset}
                limit={limit}
                total={query.data?.total ?? 0}
                onChange={setOffset}
              />
            </Panel>
          </div>
        </div>
      </div>
    </>
  )
}
